import { Image, Tooltip } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import {
  Box, Flex, Text, Menu, Button
} from '@huspy/briks-web';
import {
  formatToLocalTimeZone,
  formatNumber,
  convertToDDMMYYYYFormat,
} from '@shared/utils';
import { iconsPath } from '@shared/css.const';
import { BANK_SELECTION_RULES } from '@modules/opportunities/rules';
import { BANK_APPLICATION_STATUS } from '@modules/opportunities/entities/bankApplication/const';
import {
  CaseCreationBankDocumentsRoute,
  CaseCreationBasicInfoRoute,
  CaseCreationSubmitApplicationsRoute,
} from '@modules/opportunities/presentation/routes';
import { CaseProgressionSlido } from '@modules/opportunities/presentation/pages/CaseCreation/features/CaseProgressionSlido';
import { useDisclosure } from '@mantine/hooks';
import Table from '@components/v2/Table';
import BankIcon from '@components/v2/BankIcon';
import { Divider } from '@huspy/styled-system/jsx';
import casesTableStyles from '@modules/opportunities/presentation/pages/Cases/List/CasesTable/styles/styles';
import { OPPORTUNITY_STATUS } from '@modules/opportunities/entities/opportunity/const';
import { StatusTag } from '@components/StatusTag';
import { useGlobalStore } from '@modules/core/store';
import { OpportunityDTO } from '@modules/opportunities/entities/opportunity/api/dto/listOpportunities.dto';

const { bankIcon, bankSmallIcon } = casesTableStyles;

export const CaseTableRow = ({ opportunity }: {
  opportunity: OpportunityDTO;
}) => {
  const navigate = useNavigate();

  const [isOpen, { open, close }] = useDisclosure(false);
  const { user } = useGlobalStore();
  const opportunityId = opportunity.opportunity_external_id;
  const mainApplicantName = opportunity.main_applicant_name;
  const referrerName = `${opportunity.referrer_first_name} ${opportunity.referrer_last_name}`;

  const bankApplications = opportunity.bank_applications;
  const primaryBank = bankApplications.find(({ is_primary }) => is_primary);
  const secondaryBanks = bankApplications.filter(
    ({ is_primary }) => !is_primary
  );
  const bankApplicationId = primaryBank?.bank_application_external_id ?? '';
  const bankApplicationStatus = primaryBank?.status ?? opportunity.status;
  const numOfBankApplications = bankApplications.length;

  const isOpportunityInDraft = opportunity.status === OPPORTUNITY_STATUS.draft
    || (bankApplications.length > 0
      && bankApplications.every(
        ({ status }) => status === BANK_APPLICATION_STATUS.draft
      ));

  const isSomeCasesSubmitted = !isOpportunityInDraft
    && bankApplications.some(
      ({ status }) => status !== BANK_APPLICATION_STATUS.draft
    );

  const canSubmitAdditionalBank = opportunity.status !== OPPORTUNITY_STATUS.lost
    && isSomeCasesSubmitted
    && numOfBankApplications < BANK_SELECTION_RULES.ADDITIONAL.maxAllowedSelection;

  const isCaseOwner = user?.externalId === opportunity.referrer_external_id;

  const handleOnClick = () => {
    if (isOpportunityInDraft) {
      navigate({
        to: CaseCreationSubmitApplicationsRoute.to,
        params: { opportunityId },
        search: { bank_application_id: '' },
      });
    } else {
      open();
    }
  };

  const handleOnAdditionalBankClick = () => {
    navigate({
      to: CaseCreationBasicInfoRoute.to,
      params: { opportunityId },
      hash: 'selected_banks',
    });
  };

  const handleOnAdditionalDocumentClick = () => {
    navigate({
      to: CaseCreationBankDocumentsRoute.to,
      params: { opportunityId },
      search: { applicant: 'main' },
    });
  };

  return (
    <>
      <Table.Row
        key={ `${bankApplicationId}` }
        data-test={ `case-row-${mainApplicantName}` }
        borderTop='1px solid'
        borderColor='neutral.200'
        cursor='pointer'
        onClick={ handleOnClick }
        typeof='button'
      >
        <Table.Cell>
          <Tooltip
            label={ mainApplicantName }
            withArrow
            position='bottom-start'
            arrowOffset={ 13 }
          >
            <Box>{mainApplicantName}</Box>
          </Tooltip>
        </Table.Cell>
        <Table.Cell py='1'>
          <Flex gap='3' position='relative'>
            <Box>
              <Tooltip
                label={ primaryBank?.bank_name }
                withArrow
                position='bottom-start'
                arrowOffset={ 13 }
                style={ { display: primaryBank?.bank_name ? 'block' : 'none' } }
              >
                <Flex className={ bankIcon }>
                  <BankIcon
                    icon={ primaryBank?.bank_icon }
                    width='52'
                    height='52'
                    hasShadow
                  />
                </Flex>
              </Tooltip>
              <Flex position='absolute' bottom='-1.5' left='9'>
                {secondaryBanks.map(
                  ({ bank_application_external_id, bank_icon, bank_name }) => (
                    <Tooltip
                      key={ bank_application_external_id }
                      label={ bank_name }
                      withArrow
                      position='bottom-start'
                      arrowOffset={ 13 }
                      style={ { display: primaryBank?.bank_name ? 'block' : 'none' } }
                    >
                      <Flex className={ bankSmallIcon }>
                        <BankIcon icon={ bank_icon } hasShadow />
                      </Flex>
                    </Tooltip>
                  )
                )}
                {' '}
              </Flex>
            </Box>
          </Flex>
        </Table.Cell>
        <Table.Cell py='1'>
          <StatusTag status={ bankApplicationStatus } />
        </Table.Cell>
        <Table.Cell>{formatNumber(primaryBank?.amount, false)}</Table.Cell>
        <Table.Cell>
          <Tooltip
            label={ referrerName }
            withArrow
            position='bottom-start'
            arrowOffset={ 13 }
          >
            <Box>{referrerName}</Box>
          </Tooltip>
        </Table.Cell>
        <Table.Cell>
          {primaryBank?.created_at
            ? convertToDDMMYYYYFormat(
              formatToLocalTimeZone(opportunity.created_at)
            )
            : '-'}
        </Table.Cell>
        <Table.Cell py='1'>
          {isSomeCasesSubmitted && (
            <Menu.Root>
              <Menu.Trigger
                asChild
                onClick={ (e) => {
                  e.stopPropagation();
                } }
              >
                <Button
                  variant='subtle'
                  gap={ 1 }
                  borderRadius='full'
                  size='xs'
                  p='0'
                  data-testid='action-menu-btns'
                >
                  <Box
                    w='3px'
                    h='3px'
                    borderRadius='full'
                    background='neutral.900'
                  />
                  <Box
                    w='3px'
                    h='3px'
                    borderRadius='full'
                    background='neutral.900'
                  />
                  <Box
                    w='3px'
                    h='3px'
                    borderRadius='full'
                    background='neutral.900'
                  />
                </Button>
              </Menu.Trigger>
              <Menu.Positioner>
                <Menu.Content>
                  <Menu.ItemGroup>
                    {canSubmitAdditionalBank && (
                      <>
                        <Menu.Item
                          value='additional-bank'
                          onClick={ handleOnAdditionalBankClick }
                        >
                          <Flex gap={ 2 } align='center' justify='center' p='1'>
                            <Image
                              src={ `${iconsPath}/bank.svg` }
                              data-test='additional-bank-icon'
                            />
                            <Text size='lg'>Submit to additional bank</Text>
                          </Flex>
                        </Menu.Item>
                        <Divider />
                      </>
                    )}
                    <Menu.Item
                      value='additional-document'
                      onClick={ handleOnAdditionalDocumentClick }
                    >
                      <Flex gap={ 2 } align='center' justify='center' p='1'>
                        <Image
                          src={ `${iconsPath}/additional-case.svg` }
                          data-test='additional-documents-icon'
                        />
                        <Text size='lg'>Upload more documents</Text>
                      </Flex>
                    </Menu.Item>
                  </Menu.ItemGroup>
                </Menu.Content>
              </Menu.Positioner>
            </Menu.Root>
          )}
        </Table.Cell>
      </Table.Row>
      <CaseProgressionSlido
        opportunityId={ opportunityId }
        bankApplications={ bankApplications }
        isCaseOwner={ isCaseOwner }
        isOpen={ isOpen }
        close={ close }
      />
    </>
  );
};
